import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Activity, Ban, ReceiptText, RotateCcw, Undo2 } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useCurrency } from '@/contexts/CurrencyContext'
import type { DashboardActivityEntry, DashboardActivityKind } from '@/types'
import { cn, formatRelative } from '@/lib/utils'

// F-RPT-N44: the feed is the current business day, built server-side, so it
// survives a refresh. Four kinds only — one row per sale (split tenders folded),
// void, cancellation and refund — so it reads at a glance.

interface ActivityFeedProps {
  entries: DashboardActivityEntry[]
  businessDate?: string
  isLoading: boolean
  isError: boolean
  hasMore: boolean
  isFetchingMore: boolean
  onLoadMore: () => void
}

const KIND_META: Record<DashboardActivityKind, { icon: LucideIcon; tone: string }> = {
  sale: { icon: ReceiptText, tone: 'bg-emerald-500/10 text-emerald-600' },
  void: { icon: Undo2, tone: 'bg-amber-500/10 text-amber-600' },
  cancel: { icon: Ban, tone: 'bg-muted text-muted-foreground' },
  refund: { icon: RotateCcw, tone: 'bg-rose-500/10 text-rose-600' },
}

/** "20261024-007" → "#007"; anything without a numeric tail is shown whole. */
export function shortOrderNumber(n?: string): string {
  if (!n) return ''
  const m = /-(\d+)$/.exec(n)
  return `#${m ? m[1] : n}`
}

/** Header label for the day shown: "Today", or the date of a day still open past midnight. */
export function activityDayLabel(businessDate: string | undefined, now: Date = new Date()): string {
  if (!businessDate) return 'Today'
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  if (businessDate === today) return 'Today'
  const d = new Date(`${businessDate}T00:00:00`)
  return Number.isNaN(d.getTime())
    ? businessDate
    : d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
}

/** Re-render every 30s so "4m ago" keeps moving while the page sits open. */
function useMinuteTick() {
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 30_000)
    return () => window.clearInterval(id)
  }, [])
}

export function ActivityFeed({
  entries,
  businessDate,
  isLoading,
  isError,
  hasMore,
  isFetchingMore,
  onLoadMore,
}: ActivityFeedProps) {
  const { formatCurrency } = useCurrency()
  useMinuteTick()

  return (
    <Card className="flex h-full min-h-[420px] flex-col">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Activity className="h-4 w-4 text-emerald-600" />
            Activity
          </CardTitle>
          <span className="text-xs text-muted-foreground">{activityDayLabel(businessDate)}</span>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden p-0">
        {isLoading ? (
          <div className="space-y-3 px-4 pb-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : isError && entries.length === 0 ? (
          <div className="flex h-full min-h-[280px] items-center justify-center px-6 text-center text-sm text-muted-foreground">
            Couldn&apos;t load activity. It will retry shortly.
          </div>
        ) : entries.length === 0 ? (
          <div className="flex h-full min-h-[280px] flex-col items-center justify-center gap-2 px-6 py-8 text-center text-sm text-muted-foreground">
            <Activity className="h-5 w-5 opacity-60" />
            {businessDate ? 'No sales yet today.' : "The day hasn't been opened yet."}
          </div>
        ) : (
          <div className="max-h-[420px] overflow-y-auto">
            <ul className="divide-y divide-border">
              {entries.map((entry) => {
                const meta = KIND_META[entry.kind] ?? KIND_META.sale
                const Icon = meta.icon
                const struck = entry.kind === 'void' || entry.kind === 'cancel'
                const sub = [shortOrderNumber(entry.order_number), entry.detail].filter(Boolean).join(' · ')
                return (
                  <li key={entry.id} className="flex items-center gap-3 px-4 py-2.5">
                    <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full', meta.tone)}>
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{entry.title}</div>
                      {sub && (
                        <div className="truncate text-xs text-muted-foreground" title={entry.order_number}>
                          {sub}
                        </div>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      {entry.amount != null && entry.amount !== 0 && (
                        <div
                          className={cn(
                            'text-sm tabular-nums',
                            entry.kind === 'refund' && 'font-semibold text-rose-600',
                            entry.kind === 'sale' && 'font-semibold',
                            struck && 'text-muted-foreground line-through',
                          )}
                        >
                          {entry.amount < 0 ? `−${formatCurrency(-entry.amount)}` : formatCurrency(entry.amount)}
                        </div>
                      )}
                      <div className="text-[11px] text-muted-foreground">{formatRelative(entry.at)}</div>
                    </div>
                  </li>
                )
              })}
            </ul>
            {hasMore && (
              <div className="border-t border-border p-2 text-center">
                <button
                  type="button"
                  onClick={onLoadMore}
                  disabled={isFetchingMore}
                  className="rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-60"
                >
                  {isFetchingMore ? 'Loading…' : 'Show earlier'}
                </button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
